import React from 'react'
import type { TFunction } from 'i18next'
import { InfoTooltip } from '../../../InfoTooltip'
import { TooltipMetricExplanation } from '../../../TooltipMetricExplanation'
import { type TaxAnalysis } from '../taxService'
import { formatTaxBurden, formatCHF, formatTaxFreedomDay } from '../taxService'
import {
  ChartContainer,
  ChartTitle,
  ProfileTable,
  ProfileTableHeader,
  TableHeaderWithTooltip,
  ProfileTableRow,
  ProfileTableCell,
} from '../TaxSection.styles'

export interface ProfileDetailsCardProps {
  data: TaxAnalysis
  t: TFunction
}

export const ProfileDetailsCard: React.FC<ProfileDetailsCardProps> = ({
  data,
  t,
}) => {
  const { profiles } = data

  if (!profiles || profiles.length === 0) {
    return null
  }

  const getProfileLabel = (profile: string): string => {
    switch (profile) {
      case 'single':
        return t('profiles.single')
      case 'married':
        return t('profiles.married')
      case 'family':
        return t('profiles.family')
      default:
        return profile
    }
  }

  return (
    <ChartContainer style={{ marginBottom: 20 }}>
      <ChartTitle>{t('profileDetails.title')}</ChartTitle>
      <ProfileTable>
        <thead>
          <tr>
            <ProfileTableHeader>
              {t('profileDetails.profile')}
            </ProfileTableHeader>
            <ProfileTableHeader>
              <TableHeaderWithTooltip>
                {t('profileDetails.taxBurden')}
                <InfoTooltip>
                  <TooltipMetricExplanation
                    title={t('tooltips.taxBurden.title')}
                    shortDescription={t('tooltips.taxBurden.shortDescription')}
                    calculation={t('tooltips.taxBurden.calculation')}
                    importance={t('tooltips.taxBurden.importance')}
                    limitations={t('tooltips.taxBurden.limitations')}
                    source={t('tooltips.taxBurden.source')}
                  />
                </InfoTooltip>
              </TableHeaderWithTooltip>
            </ProfileTableHeader>
            <ProfileTableHeader>
              <TableHeaderWithTooltip>
                {t('profileDetails.annualTax')}
                <InfoTooltip>
                  <TooltipMetricExplanation
                    title={t('tooltips.annualTax.title')}
                    shortDescription={t('tooltips.annualTax.shortDescription')}
                    calculation={t('tooltips.annualTax.calculation')}
                    importance={t('tooltips.annualTax.importance')}
                    limitations={t('tooltips.annualTax.limitations')}
                    source={t('tooltips.annualTax.source')}
                  />
                </InfoTooltip>
              </TableHeaderWithTooltip>
            </ProfileTableHeader>
            <ProfileTableHeader>
              <TableHeaderWithTooltip>
                {t('profileDetails.taxFreedomDay')}
                <InfoTooltip>
                  <TooltipMetricExplanation
                    title={t('tooltips.taxFreedomDay.title')}
                    shortDescription={t(
                      'tooltips.taxFreedomDay.shortDescription'
                    )}
                    calculation={t('tooltips.taxFreedomDay.calculation')}
                    importance={t('tooltips.taxFreedomDay.importance')}
                    limitations={t('tooltips.taxFreedomDay.limitations')}
                    source={t('tooltips.taxFreedomDay.source')}
                  />
                </InfoTooltip>
              </TableHeaderWithTooltip>
            </ProfileTableHeader>
          </tr>
        </thead>
        <tbody>
          {profiles.map((profile) => (
            <ProfileTableRow key={profile.profile}>
              <ProfileTableCell>
                <strong>{getProfileLabel(profile.profile)}</strong>
              </ProfileTableCell>
              <ProfileTableCell>
                {formatTaxBurden(profile.tax_burden)}
              </ProfileTableCell>
              <ProfileTableCell>
                {formatCHF(profile.total_tax)}
              </ProfileTableCell>
              <ProfileTableCell>
                {formatTaxFreedomDay(profile.tax_freedom_day)}
              </ProfileTableCell>
            </ProfileTableRow>
          ))}
        </tbody>
      </ProfileTable>
    </ChartContainer>
  )
}
